// src/components/RequireFullMemberOrRole.jsx
import React from "react";
import { Link, Navigate } from "react-router-dom";
import useUserProfile from "../hooks/useUserProfile";
import useCoopMemberRole from "../hooks/useCoopMemberRole";

/**
 * Gate access to children unless the user is:
 * - Cooperative membership status === 'approved', or
 * - has any role in rolesAllowed (default: ['admin', 'treasurer'])
 * Suspended users are denied.
 * 
 * This component now supports both legacy membershipStatus and new coopMembers system
 * for backward compatibility during migration.
 */
export default function RequireFullMemberOrRole({ rolesAllowed = ["admin", "treasurer"], children }) {
  const { loading: profileLoading, profile, isApprovedMember, membershipStatus } = useUserProfile();
  
  // Check if any of the allowed roles are delivery-related
  const hasDeliveryRoles = rolesAllowed.some(role => ['delivery', 'rider'].includes(role));
  
  // Only fetch cooperative member roles if we need them (not for delivery-only routes)
  const shouldFetchCoopRoles = !hasDeliveryRoles;
  const { coopRole, hasAnyRole, loading: roleLoading } = useCoopMemberRole(shouldFetchCoopRoles);

  // Show loading state only if we're actively loading profile data
  // Don't block the UI for role loading as it's less critical
  if (profileLoading) {
    return (
      <div className="page-gutter">
        <div className="max-w-xl mx-auto card p-6 text-center">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (profile?.suspended === true) {
    return (
      <div className="page-gutter">
        <div className="max-w-xl mx-auto card p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-ink/70">Your account is currently suspended.</p>
        </div>
      </div>
    );
  }

  // Check cooperative member roles from the new system
  // If role loading is still in progress, assume no role for now (safer approach)
  const isRoleAllowed = roleLoading ? false : hasAnyRole(rolesAllowed);
  
  // Check both legacy and new membership systems for backward compatibility
  const isLegacyFull = String(profile?.membershipStatus || "").toLowerCase() === "full";
  const isNewApproved = isApprovedMember;
  
  // Legacy support: also check for 'member' role (will be removed after migration)
  const hasLegacyMemberRole = coopRole === 'member';
  
  const hasAccess = isRoleAllowed || isLegacyFull || isNewApproved || hasLegacyMemberRole;

  if (hasAccess) return children;

  // Deny in-place with CTA instead of redirect
  return (
    <div className="page-gutter">
      <div className="max-w-xl mx-auto card p-6 text-center">
        <h2 className="text-xl font-semibold mb-3">You’re almost there</h2>
        <p className="text-ink/80 mb-4">
          {membershipStatus === 'pending' 
            ? 'Your cooperative membership application is pending approval.'
            : 'Apply for cooperative membership to unlock Reports and member benefits.'
          }
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          {membershipStatus === 'pending' ? (
            <Link className="btn btn-outline" to="/profile">View Application Status</Link>
          ) : (
            <Link className="btn btn-primary" to="/become-member">Apply for Membership</Link>
          )}
        </div>
      </div>
    </div>
  );
}
